import { McpPanel } from "@/components/McpPanel";
import { t } from "@/lib/i18n";

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Robot, PaperPlaneRight, Microphone, Paperclip, Trash, Plugs, UserCircle, X } from "@phosphor-icons/react";
import dayjs from "dayjs";
import { toast } from "sonner";
import { MemoryChip } from "@/components/MemoryChip";
import { useSpeech } from "@/hooks/useSpeech";
import api, { errDetail } from "@/lib/api";

export const AgentPanel = ({ threadId, refreshEmails, mailbox = "" }) => {
  const mbq = mailbox ? { params: { mailbox } } : {};
  const navigate = useNavigate();
  const [tab, setTab] = useState("agent");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [undone, setUndone] = useState({});
  const [draftDone, setDraftDone] = useState({});
  const sendDraftCard = async (m) => {
    try {
      await api.post(`/drafts/${m.draft_id}/send`, null, mbq);
      setDraftDone((d) => ({ ...d, [m.draft_id]: "sent" }));
      toast.success(t("agent.draft_sent", "Email sent"));
      refreshEmails?.();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const discardDraftCard = async (m) => {
    try {
      await api.delete(`/drafts/${m.draft_id}`);
      setDraftDone((d) => ({ ...d, [m.draft_id]: "discarded" }));
      toast.success(t("agent.draft_discarded", "Draft discarded"));
      refreshEmails?.();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const undoOp = async (opId) => {
    try {
      await api.post(`/emails/undo/${opId}`);
      setUndone((u) => ({ ...u, [opId]: true }));
      toast.success("Undone — emails restored");
      refreshEmails?.();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const { listening, toggleMic } = useSpeech((final) => setInput((prev) => (prev + " " + final).trim()));
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/agent/messages${threadId ? `?thread_id=${threadId}` : ""}`, mbq); setMessages(data); } catch {}
  }, [threadId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    const localUser = { id: `tmp-${Date.now()}`, kind: "user", content: msg, attachments: attachments.map((f) => f.name) };
    setMessages((m) => [...m, localUser]);
    setSending(true);
    try {
      const { data } = await api.post("/agent/chat", { message: msg, thread_id: threadId, attachments: attachments.map((f) => f.name) }, mbq);
      setMessages((m) => [...m, ...data.messages]);
      setAttachments([]);
      if (data.messages.some((x) => x.kind === "action")) refreshEmails?.();
      import("@/components/TokenWidget").then((m) => m.refreshTokenWidget());
    } catch (e) { toast.error(errDetail(e)); }
    setSending(false);
  };

  useEffect(() => {
    const handler = (e) => { if (e.detail?.text) send(e.detail.text); };
    window.addEventListener("agent-ask", handler);
    return () => window.removeEventListener("agent-ask", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, input, attachments, sending]);

  const clearChat = async () => {
    try { await api.delete("/agent/messages"); setMessages([]); } catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <div className="w-[340px] shrink-0 h-full flex flex-col bg-slate-50">
      <div className="flex items-center border-b border-gray-200 bg-white">
        <button data-testid="agent-tab" onClick={() => setTab("agent")}
          className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === "agent" ? "border-blue-600 text-gray-900" : "border-transparent text-gray-400 hover:text-gray-700"}`}>
          <Robot size={15} /> Agent
        </button>
        <button data-testid="mcp-tab" onClick={() => setTab("mcp")}
          className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === "mcp" ? "border-blue-600 text-gray-900" : "border-transparent text-gray-400 hover:text-gray-700"}`}>
          <Plugs size={15} /> MCP
        </button>
      </div>

      {tab === "mcp" ? (
        <McpPanel />
      ) : (
        <>
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 bg-white">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[9px] font-bold">AI</span>
              <span className="text-sm font-semibold text-gray-800">{t("agent.title", "Email Agent")}</span>
            </div>
            <button data-testid="clear-agent-chat-btn" onClick={clearChat} className="text-gray-400 hover:text-red-600 transition-colors p-1"><Trash size={15} /></button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto scroll-thin p-3 space-y-2.5">
            {messages.length === 0 && (
              <div data-testid="agent-empty-state" className="text-xs text-gray-400 bg-white border border-gray-200 rounded-md p-3 leading-relaxed">
                <span className="font-semibold text-gray-600 block mb-1">{t("agent.try_asking", "Try asking me:")}</span>
                "Draft a reply to this email" · "Delete all promotional emails" · "Schedule a meeting with Elena next Tuesday" · "Remember that John prefers short replies"
              </div>
            )}
            {messages.map((m) => (
              m.kind === "user" ? (
                <div key={m.id} className="flex justify-end fade-up">
                  <div data-testid="agent-msg-user" className="max-w-[85%] bg-blue-600 text-white rounded-lg rounded-br-sm px-3 py-2 text-[13px] leading-relaxed">
                    {m.content}
                    {m.attachments?.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {m.attachments.map((a, i) => (
                          <span key={i} className="flex items-center gap-1 bg-blue-500 rounded px-1.5 py-0.5 text-[11px]"><Paperclip size={10} />{a}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <UserCircle size={18} className="text-blue-400 ml-1.5 mt-1 shrink-0" />
                </div>
              ) : m.kind === "action" ? (
                <div key={m.id} className="flex fade-up">
                  <span className="w-5 h-5 rounded bg-gray-200 flex items-center justify-center mr-1.5 mt-1 shrink-0"><Robot size={12} className="text-gray-600" /></span>
                  <div data-testid="agent-msg-action" className="max-w-[85%] bg-white border border-gray-200 rounded-lg px-3 py-2 text-[13px] text-gray-800">
                    {m.content}
                    {m.draft_id && m.body && (
                      <div data-testid="agent-draft-card" className="mt-2 border border-blue-100 bg-blue-50/60 rounded-md p-2">
                        <div className="text-[11px] text-gray-500">{t("agent.draft_to", "To")}: <span data-testid="agent-draft-to" className="font-medium text-gray-800">{m.to_email}</span></div>
                        <div data-testid="agent-draft-subject" className="text-xs font-semibold text-gray-900 mt-0.5">{m.subject}</div>
                        <div data-testid="agent-draft-body" className="mt-1 max-h-36 overflow-y-auto scroll-thin text-xs text-gray-700 whitespace-pre-wrap bg-white border border-gray-100 rounded p-2">{m.body}</div>
                        {draftDone[m.draft_id] ? (
                          <div data-testid="agent-draft-status" className="mt-1.5 text-xs font-medium text-green-600">{draftDone[m.draft_id] === "sent" ? "✓ " + t("agent.sent", "Sent") : "✓ " + t("agent.discarded", "Discarded")}</div>
                        ) : (
                          <div className="flex gap-1.5 mt-2">
                            <button data-testid="agent-draft-send-btn" onClick={() => sendDraftCard(m)}
                              className="bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-semibold rounded px-2.5 py-1 transition-colors">{t("thread.send", "Send")}</button>
                            <button data-testid="agent-draft-edit-btn" onClick={() => navigate(`/compose?draft_id=${m.draft_id}`)}
                              className="border border-gray-300 hover:bg-gray-50 text-gray-700 text-[11px] font-medium rounded px-2.5 py-1 transition-colors">{t("thread.edit", "Edit")}</button>
                            <button data-testid="agent-draft-discard-btn" onClick={() => discardDraftCard(m)}
                              className="text-gray-500 hover:text-red-600 text-[11px] font-medium rounded px-2 py-1 transition-colors">{t("thread.discard", "Discard")}</button>
                          </div>
                        )}
                      </div>
                    )}
                    {m.op_id && !undone[m.op_id] && (
                      <button data-testid="agent-undo-btn" onClick={() => undoOp(m.op_id)}
                        className="block mt-1.5 text-xs font-semibold text-red-600 border border-red-200 rounded-md px-2 py-1 hover:bg-red-50 transition-colors">
                        ↩ Undo (10 min)
                      </button>
                    )}
                    {m.op_id && undone[m.op_id] && <div className="mt-1 text-xs text-green-600 font-medium">✓ Undone</div>}
                    {m.link && (
                      <a href={m.link} target="_blank" rel="noreferrer" data-testid="agent-action-link"
                         className="block mt-1 text-blue-600 hover:underline text-xs font-medium">
                        Open in Google Calendar ↗
                      </a>
                    )}
                  </div>
                </div>
              ) : (
                <div key={m.id} className="flex fade-up">
                  <span className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mr-1.5 mt-1 shrink-0 text-[8px] font-bold text-blue-700">AI</span>
                  <div data-testid="agent-msg-agent" className="max-w-[85%] bg-blue-50 border border-blue-100 text-blue-950 rounded-lg px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap">
                    {m.content}
                    {m.email_results?.length > 0 && (
                      <div className="mt-2 space-y-1.5">
                        {m.email_results.map((r) => (
                          <button key={r.id} data-testid="agent-email-card"
                            onClick={() => window.dispatchEvent(new CustomEvent("open-thread", { detail: { threadId: r.thread_id } }))}
                            className="w-full text-left bg-white border border-blue-200 rounded-md px-2.5 py-1.5 hover:border-blue-400 transition-colors">
                            <div className="flex items-center gap-1.5 text-[12px]">
                              {!r.is_read && <span className="w-1.5 h-1.5 rounded-full bg-blue-600 shrink-0" />}
                              <span className="font-semibold text-gray-900 truncate">{r.from_name || r.from_email}</span>
                              <span className="text-gray-400 ml-auto shrink-0">{dayjs(r.created_at).format("MMM D")}</span>
                            </div>
                            <div className="text-[12px] text-gray-600 truncate">{r.subject}</div>
                          </button>
                        ))}
                      </div>
                    )}
                    {m.sources > 0 && (
                      <div data-testid="agent-sources-chip" className="mt-1.5 text-[10px] text-blue-500 font-medium">
                        ⚑ sourced from {m.sources} email{m.sources > 1 ? "s" : ""}
                      </div>
                    )}
                    <MemoryChip memories={m.memories_used} />
                  </div>
                </div>
              )
            ))}
            {sending && (
              <div className="flex items-center gap-1.5 text-xs text-gray-400 pl-7" data-testid="agent-thinking">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" />
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce [animation-delay:120ms]" />
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce [animation-delay:240ms]" />
                thinking…
              </div>
            )}
          </div>

          <div className="p-3 bg-white border-t border-gray-200">
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {attachments.map((f, i) => (
                  <span key={i} data-testid={`attachment-chip-${i}`} className="flex items-center gap-1 bg-gray-100 border border-gray-200 rounded-md px-2 py-1 text-xs text-gray-700">
                    <Paperclip size={11} /> {f.name}
                    <button onClick={() => setAttachments(attachments.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500"><X size={11} /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-end gap-1.5 border border-gray-300 rounded-lg px-2 py-1.5 focus-within:ring-2 focus-within:ring-blue-500">
              <button data-testid="agent-attach-btn" onClick={() => fileRef.current?.click()} className="p-1 text-gray-400 hover:text-gray-700 transition-colors" title={t("common.attach_files", "Attach files")}>
                <Paperclip size={17} />
              </button>
              <input ref={fileRef} type="file" multiple className="hidden" data-testid="agent-file-input"
                onChange={(e) => { setAttachments([...attachments, ...Array.from(e.target.files)]); e.target.value = ""; }} />
              <textarea data-testid="agent-input" rows={1} value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={listening ? t("agent.listening", "Listening…") : t("agent.placeholder", "Ask your email agent...")}
                className="flex-1 resize-none text-sm py-1 focus:outline-none max-h-28" />
              <button data-testid="agent-mic-btn" onClick={toggleMic} title={t("common.speech_to_text", "Speech to text")}
                className={`p-1 transition-colors ${listening ? "mic-active" : "text-gray-400 hover:text-gray-700"}`}>
                <Microphone size={17} weight={listening ? "fill" : "regular"} />
              </button>
              <button data-testid="agent-send-btn" onClick={() => send()} disabled={sending || !input.trim()}
                className="p-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-40">
                <PaperPlaneRight size={15} weight="bold" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
